<template>
    <LaBaseCard>
        <div v-if="loading">Loading data...</div>
        <div v-else-if="error">Error occurred: {{ error }}</div>
        <div v-else-if="data">
            <h2 class="page-heading mt-0" data-test="heading">Ads.txt analysis for domain: {{ website.domain }}</h2>
            <div class="columns">
                <div :key="'submit_button'" class="column filter">
                    <LaBaseButton data-test="check-now-button" :disabled="loading" text="Check Now" @click="checkNow" />
                </div>
                <p v-if="data">last check: {{ formatDateTime(data.checked) }}</p>
            </div>

            <template v-if="data.error && data.error.length">
                <div class="page-header mt-0">
                    <h3 class="page-heading mt-0" data-test="heading">
                        <span class="error">⚠</span>
                        Error:
                    </h3>
                </div>
                <div class="copy-code-container" id="error">
                    <div class="copy-code-content">
                        <ul v-if="data.error.length">
                            <li v-for="(item, index) in data.error" :key="index">{{ item }}</li>
                        </ul>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="page-header mt-0">
                    <h3 class="page-heading mt-0" data-test="heading">
                        <span v-if="data.missing && data.missing.length" class="error">⚠</span>
                        Missing data:
                    </h3>
                </div>
                <div class="copy-code-container" id="missing">
                    <div class="copy-code-content">
                        <ul v-if="data.missing && data.missing.length">
                            <li v-for="(item, index) in data.missing" :key="index">{{ item }}</li>
                        </ul>
                        <p v-else>Everything is set up correctly.</p>
                    </div>
                </div>
                <div class="page-header mt-0">
                    <h3 class="page-heading mt-0" data-test="heading">
                        <span v-if="data.duplicate && data.duplicate.length" class="warning">⚠</span>
                        Duplicate data:
                    </h3>
                </div>
                <div class="copy-code-container" id="duplicate">
                    <div class="copy-code-content">
                        <ul v-if="data.duplicate && data.duplicate.length">
                            <li v-for="(item, index) in data.duplicate" :key="index">{{ item }}</li>
                        </ul>
                        <p v-else>No duplicates found.</p>
                    </div>
                </div>
            </template>
        </div>
    </LaBaseCard>
</template>
<script>
import { computed, watch } from "vue";
import LaBaseButton from "@/components/button/BaseButton";
import LaBaseCard from "@/components/BaseCard.vue";
import store from "@/store/store";
import { actionTypes as adstxtActionTypes, getterTypes as adstxtGetterTypes } from "@/store/Adstxt/types";
import { formatDateTime } from "@/helpers/filters.js";

export default {
    name: "DataDisplay",
    components: {
        LaBaseCard,
        LaBaseButton,
    },
    props: {
        website: {
            required: true,
            type: Object,
        },
    },
    setup(props) {
        let data = computed(() => store.getters[adstxtGetterTypes.getAdstxtValidationResult]);
        let loading = computed(() => store.getters[adstxtGetterTypes.isLoading]);
        let error = computed(() => store.getters[adstxtGetterTypes.getError]);

        if (!data.value || !data.value.data) {
            store.dispatch(adstxtActionTypes.getValidationResult, {
                id: props.website.id,
                domain: props.website.domain,
            });
        }

        const checkNow = () => {
            store.dispatch(adstxtActionTypes.getValidationResult, {
                id: props.website.id,
                domain: props.website.domain,
                force: true,
            });
        };

        watch(
            () => props.website,
            (newWebsite, oldWebsite) => {
                if (newWebsite.domain !== oldWebsite?.domain) {
                    store.dispatch(adstxtActionTypes.getValidationResult, {
                        id: props.website.id,
                        domain: props.website.domain,
                    });
                }
            },
            { deep: true }
        );
        return {
            data,
            loading,
            error,
            formatDateTime,
            checkNow,
        };
    },
};
</script>
<style lang="scss" scoped>
.text {
    margin-bottom: 0.5rem;
    &-container {
        margin-bottom: 2rem;
    }
}
.copy-code {
    &-content {
        font-family: Courier New;
        @include --big-mobile-viewport {
            font-size: 0.9rem;
        }
        @include --mobile-viewport {
            font-size: 0.8rem;
        }
        ul {
            list-style-type: none;
        }
    }
    &-container {
        word-wrap: break-word;
        word-break: break-all;
        white-space: pre-wrap;
        background-color: $blue;
        border-radius: $normal-radius;
        padding: 2rem;
        color: $dark-grey;
    }
}
#vendors-table {
    border-collapse: collapse;
}
#vendors-table th:first-child,
#vendors-table td:first-child {
    width: 50px;
}
#vendors-table th:nth-child(2),
#vendors-table td:nth-child(2) {
    width: 250px;
}
#vendors-table th,
#vendors-table td {
    border: 1px solid gray;
    padding: 10px;
}
body.dark {
    .copy-code {
        &-container {
            background-color: $light-black;
        }
    }
    strong {
        background: $green-2;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }
}
</style>
<style lang="scss" scoped>
.page {
    &-header {
        padding: 1.5rem 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        @include --big-mobile-viewport {
            padding: 0.5rem 0;
        }
    }
    &-heading {
        font-weight: 600;
        line-height: 1.3;
        font-size: 1.5rem;
        padding-left: 0.5rem;
        color: $blue;
    }
    &-pickers {
        display: flex;
    }
}
span.error {
    -webkit-text-fill-color: #f00;
}
span.warning {
    -webkit-text-fill-color: #ff0;
}
body.dark {
    .page {
        &-heading {
            padding-left: 0.5rem;
            background: $green-2;
            background-clip: text;
            -webkit-text-fill-color: transparent;
        }
    }
}
</style>
